import { useCallback, useEffect, useRef, useState } from 'react'
import Hls from 'hls.js'

const playerEnded = (e: Event) => {
  if (e.target) {
    ;(e.target as HTMLVideoElement).play()
  }
}
const playerLoaded = (e: Event) => {
  if (e.target) {
    ;(e.target as Element).classList.add('loaded')
  }
}

const playerPlay = (e: Event) => {
  if (e.target) {
    ;(e.target as Element).classList.add('video')
  }
}

const playerPause = (e: Event) => {
  if (e.target) {
    ;(e.target as Element).classList.remove('video')
  }
}

export const useAutoPlay = (autoPlay: boolean) => {
  const mediaRef = useRef<HTMLDivElement>(null)
  const [videoEl, setVideoEl] = useState<Element>()
  const timeoutId = useRef<NodeJS.Timeout>()

  const handleInterSection = useCallback(
    (hls?: Hls) => {
      let promise: Promise<void> | undefined
      const _videoEl = videoEl as HTMLVideoElement
      return new IntersectionObserver(
        (entires, observer) => {
          timeoutId.current && clearTimeout(timeoutId.current)
          timeoutId.current = setTimeout(() => {
            if (entires[0].isIntersecting) {
              hls?.startLoad()
              if (_videoEl.paused) {
                promise = _videoEl.play()
              }
            } else {
              hls?.stopLoad()
              if (promise !== undefined) {
                promise.then(() => {
                  if (_videoEl.played) {
                    _videoEl.pause()
                  }
                })
              }
            }
          }, 1000)
        },
        { root: null, threshold: 1, rootMargin: '0px 0px 0px 0px' },
      )
    },
    [videoEl],
  )

  useEffect(() => {
    if (!videoEl) return
    const src = (videoEl as HTMLVideoElement).dataset.src
    if (!src) return
    if (/.mp4$/.test(src)) {
      ;(videoEl as HTMLVideoElement).src = src
      handleInterSection().observe(mediaRef.current as HTMLElement)
    } else if (Hls.isSupported()) {
      const hls = new Hls({
        debug: false,
        maxBufferSize: 1000 * 1000 * 5,
        maxBufferLength: 5,
      })
      hls.attachMedia(videoEl as HTMLVideoElement)
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src)
      })
      hls.on(Hls.Events.MANIFEST_PARSED, function (event, data) {
        hls.stopLoad()
      })
      handleInterSection(hls).observe(mediaRef.current as HTMLElement)
    } else if ((videoEl as HTMLVideoElement)?.canPlayType('application/vnd.apple.mpegurl')) {
      ;(videoEl as HTMLVideoElement).src = src
      handleInterSection().observe(mediaRef.current as HTMLElement)
    }
  }, [videoEl, handleInterSection])

  useEffect(() => {
    return () => {
      timeoutId.current && clearTimeout(timeoutId.current)
    }
  }, [timeoutId])

  useEffect(() => {
    if (!autoPlay) return
    if (mediaRef.current) {
      const videoEl = mediaRef.current.querySelector('video') as HTMLVideoElement
      setVideoEl(videoEl)
      videoEl.addEventListener('ended', playerEnded)
      videoEl.addEventListener('loadeddata', playerLoaded)
      videoEl.addEventListener('play', playerPlay)
      videoEl.addEventListener('pause', playerPause)

      return () => {
        videoEl.removeEventListener('ended', playerEnded)
        videoEl.removeEventListener('loadeddata', playerLoaded)
        videoEl.removeEventListener('play', playerPlay)
        videoEl.removeEventListener('pause', playerPause)
      }
    }
  }, [autoPlay])

  return { mediaRef }
}
